import { logger } from '#app/services/logger.js';
import { serializeError } from '@contra/utilities/serializeError';

type StorageType = 'localStorage' | 'sessionStorage';

const log = logger.child({
  namespace: 'utilities:storage',
});

export type StorageKey = `CONTRA_LOCAL_${string}` | `CONTRA_SESSION_${string}`;

const assertValidName = (name: string, storageType: StorageType) => {
  const requiredPrefix =
    storageType === 'localStorage' ? 'CONTRA_LOCAL_' : 'CONTRA_SESSION_';

  if (!name.startsWith(requiredPrefix)) {
    throw new Error(
      'Invalid storage variable name "' +
        name +
        '". Must have ' +
        requiredPrefix +
        ' prefix.',
    );
  }

  if (name.toUpperCase() !== name) {
    throw new Error(
      'Invalid storage variable name "' + name + '". Must be uppercase.',
    );
  }
};

/**
 * @property ignoreNamingConventions Only to be used when session/local storage is needed to control a third-party, e.g. @roarr/browser-log-write.
 */
type Configuration = {
  ignoreNamingConventions: boolean;
};

const createStorage = (storageType: StorageType = 'localStorage') => {
  const storageAdapter =
    typeof window === 'undefined' ? null : window[storageType];

  return {
    clear: (): void => {
      if (!storageAdapter) {
        log.warn('storage is uninitiated; clear() is a noop');

        return;
      }

      try {
        storageAdapter?.clear();
      } catch (error) {
        log.error(
          {
            error: serializeError(error),
          },
          `failed to clear ${storageType} storage`,
        );
      }
    },

    getItem: <T extends Configuration>(
      key: T['ignoreNamingConventions'] extends true ? string : StorageKey,
      configuration?: T,
    ): null | string => {
      if (!configuration?.ignoreNamingConventions) {
        assertValidName(key, storageType);
      }

      if (!storageAdapter) {
        log.warn('storage is uninitiated; getItem() is a noop');

        return null;
      }

      try {
        return storageAdapter?.getItem(key);
      } catch (error) {
        log.error(
          { error: serializeError(error), key },
          `failed to get ${storageType} storage item`,
        );
      }

      return null;
    },

    removeItem: <T extends Configuration>(
      key: T['ignoreNamingConventions'] extends true ? string : StorageKey,
      configuration?: T,
    ): void => {
      if (!configuration?.ignoreNamingConventions) {
        assertValidName(key, storageType);
      }

      if (!storageAdapter) {
        log.warn('storage is uninitiated; removeItem() is a noop');

        return;
      }

      try {
        storageAdapter?.removeItem(key);
      } catch (error) {
        log.error(
          { error: serializeError(error), key },
          `failed to remove ${storageType} storage item`,
        );
      }
    },

    setItem: <T extends Configuration>(
      key: T['ignoreNamingConventions'] extends true ? string : StorageKey,
      value: string,
      configuration?: T,
    ): void => {
      if (!configuration?.ignoreNamingConventions) {
        assertValidName(key, storageType);
      }

      if (!storageAdapter) {
        log.warn('storage is uninitiated; setItem() is a noop');

        return;
      }

      try {
        storageAdapter?.setItem(key, value);
      } catch (error) {
        log.error(
          { error: serializeError(error), key, value },
          `failed to set ${storageType} storage item`,
        );
      }
    },
  };
};

/**
 * @see https://contra-work.slack.com/archives/C016ND05C2Z/p1677875624360389
 */
export const localStorage = createStorage('localStorage');
export const sessionStorage = createStorage('sessionStorage');
